import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Spinner, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const getAuthHeaders = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const OdemeYonetimi = () => {
  document.title = "Ödeme Yönetimi | HarmanaGEL";

  const [odemeler, setOdemeler] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOdemeler = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${BASE_URL}/ODEME?$top=500&$orderby=ODEME_TARIHI desc&$expand=SIPARIS($select=ID)`,
        { headers: getAuthHeaders() }
      );
      const result = await response.json();
      if (result && result.value) setOdemeler(result.value);
    } catch (err) {
      setError("Ödemeler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOdemeler();
  }, []);

  const durumBadge = (durum) => {
    const renkler = { "Basarili": "success", "Basarisiz": "danger", "Beklemede": "warning", "Iade": "info", "Basarılı": "success", "Başarısız": "danger", "Başarılı": "success" };
    return <Badge color={renkler[durum] || "secondary"}>{durum || "Bilinmiyor"}</Badge>;
  };

  const toplamOdeme = useMemo(() => odemeler.reduce((s, o) => s + parseFloat(o.ODEME_MIKTARI || 0), 0), [odemeler]);
  const basariliOdeme = useMemo(() => odemeler.filter(o => (o.ODEME_DURUMU || "").toLowerCase().includes("başar") || (o.ODEME_DURUMU || "").toLowerCase().includes("basar")).length, [odemeler]);

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
    },
    {
      header: "Sipariş ID",
      accessorKey: "SIPARIS_ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-medium">#{cell.getValue()}</span>,
    },
    {
      header: "Ödeme Tipi",
      accessorKey: "ODEME_TIP",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cell) => <Badge color="primary">{cell.getValue()}</Badge>,
    },
    {
      header: "Miktar",
      accessorKey: "ODEME_MIKTARI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => (
        <span className="fw-bold text-success fs-5">
          {parseFloat(cell.getValue() || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
        </span>
      ),
    },
    {
      header: "Durum",
      accessorKey: "ODEME_DURUMU",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cell) => durumBadge(cell.getValue()),
    },
    {
      header: "Referans No",
      accessorKey: "REFERANS_NO",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => <span className="font-monospace small">{cell.getValue() || "-"}</span>,
    },
    {
      header: "Ödeme Tarihi",
      accessorKey: "ODEME_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleString("tr-TR") : "-";
      },
    },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Ödeme Yönetimi" />

          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row className="mb-3">
            <Col md={4}>
              <Card className="bg-primary text-white">
                <CardBody className="d-flex align-items-center gap-3">
                  <i className="bx bx-credit-card fs-1" />
                  <div>
                    <h5 className="mb-0">{odemeler.length}</h5>
                    <small>Toplam Ödeme Kaydı</small>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="bg-success text-white">
                <CardBody className="d-flex align-items-center gap-3">
                  <i className="bx bx-check-circle fs-1" />
                  <div>
                    <h5 className="mb-0">{basariliOdeme}</h5>
                    <small>Başarılı Ödeme</small>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="bg-info text-white">
                <CardBody className="d-flex align-items-center gap-3">
                  <i className="bx bx-money fs-1" />
                  <div>
                    <h5 className="mb-0">{toplamOdeme.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</h5>
                    <small>Toplam Ödeme Tutarı</small>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">Ödemeler</h4>
                    <Button color="primary" size="sm" onClick={fetchOdemeler} disabled={isLoading}>
                      <i className="bx bx-refresh me-1" />
                      Yenile
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={odemeler}
                      isGlobalFilter={true}
                      SearchPlaceholder="Ödeme ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default OdemeYonetimi;
